import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { LogIn, Database } from 'lucide-react';

const SSO_ERRORS = {
  not_configured: 'Microsoft sign-in is not configured.',
  denied: 'Microsoft sign-in was cancelled.',
  no_account:
    'No DelAir account matches this Microsoft login. Ask an admin to create one with this email.',
  inactive: 'This account is inactive.',
  failed: 'Microsoft sign-in failed. Try again.',
};

const MicrosoftLogo = () => (
  <svg className="h-4 w-4 mr-2" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [microsoftEnabled, setMicrosoftEnabled] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toastedSsoError = useRef(false);

  useEffect(() => {
    const code = searchParams.get('sso_error');
    if (!code || toastedSsoError.current) return;
    toastedSsoError.current = true;
    toast.error(SSO_ERRORS[code] || SSO_ERRORS.failed);
    navigate('/login', { replace: true });
  }, [searchParams, navigate]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/auth/microsoft')
      .then((res) => {
        if (!cancelled) setMicrosoftEnabled(Boolean(res.data?.enabled));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(username, password);
      navigate('/');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-indigo-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Database className="h-12 w-12 text-delair-blue" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">DelAir Dashboard</h2>
          <p className="text-gray-600 mt-2">Data Integration System</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700">
              Username
            </label>
            <input
              id="username"
              name="username"
              type="text"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-delair-blue focus:border-delair-blue"
              placeholder="Enter your username"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-delair-blue focus:border-delair-blue"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-delair-blue hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-delair-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="loading-spinner mr-2"></div>
            ) : (
              <LogIn className="h-4 w-4 mr-2" />
            )}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {microsoftEnabled && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>
            <a
              href="/api/auth/microsoft/login"
              className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-delair-blue"
            >
              <MicrosoftLogo />
              Sign in with Microsoft
            </a>
          </>
        )}

      </div>
    </div>
  );
};

export default Login;
